import React, { useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';
import { reportError } from '@modernloop/shared/observability';
import { getExternalErrorMessage } from '@modernloop/shared/utils';
import { useSnackbar } from 'notistack';

import { useActivityFeedRefetch } from 'src/hooks/useActivityFeedRefetch';
import useDirectoryInfo from 'src/hooks/useDirectoryInfo';
import useRefetchScheduleTaskQuery from 'src/hooks/useRefetchScheduleTaskQuery';

import cancelDebrief from 'src/utils/api/cancelDebrief';
import { apolloCacheDeleteById } from 'src/utils/apolloHelpers';

type Props = {
  interviewId: string;
  onClose: () => void;
};

const DebriefContentCancelDebriefDialog = ({ interviewId, onClose }: Props) => {
  const { t } = useTranslation();
  const directoryInfo = useDirectoryInfo();

  const [canceling, setCanceling] = useState(false);

  const client = useApolloClient();

  const { enqueueSnackbar } = useSnackbar();
  const refetchActivityFeed = useActivityFeedRefetch();
  const refetchScheduleTaskQuery = useRefetchScheduleTaskQuery();

  const handleCancelEvent = async () => {
    setCanceling(true);
    // eslint-disable-next-line promise/catch-or-return
    cancelDebrief({ applicationDebriefId: interviewId })
      .then(() => {
        refetchScheduleTaskQuery();
        refetchActivityFeed();
        onClose();
        apolloCacheDeleteById(client.cache, interviewId, 'ApplicationDebrief');
        return undefined; // Returning to prevent eslint error
      })
      .catch((error) => {
        reportError(error);
        enqueueSnackbar(getExternalErrorMessage(error), { variant: 'error' });
      })
      .finally(() => setCanceling(false));
  };

  return (
    <Dialog
      dataTestId="debrief-cancel-dialog"
      open
      title={t('hiring-dashboard-schedule:schedule.cancelDialog.title')}
      subTitle={t('hiring-dashboard-schedule:schedule.cancelDialog.subtitle')}
      onClose={onClose}
      submitOptions={{
        label: t('hiring-dashboard-schedule:schedule.content.cancelEvent'),
        onClick: handleCancelEvent,
        isDangerous: true,
        isDisabled: canceling,
        isLoading: canceling,
      }}
      cancelOptions={{
        dataTestId: 'debrief-cancel-dialog-keep-event-button',
        label: t('hiring-dashboard-schedule:schedule.content.keepEvent'),
        onClick: onClose,
        isDisabled: canceling,
      }}
    >
      {t('hiring-dashboard-schedule:schedule.cancelDebrief.deleteWarning', { calendarName: directoryInfo.label })}
      <br />
      {t('hiring-dashboard-schedule:schedule.cancelDebrief.noCommunication')}
    </Dialog>
  );
};

export default DebriefContentCancelDebriefDialog;
